/**
 * @package   EGI / Frontend — cart
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-385 passo 3)
 * @date      2026-07-05
 * @purpose   Modulo carrello dell'isola wishlist (ADR-C6: STESSA isola, un solo
 *            boot, contatori accoppiati). Server-only (ADR-C7): guest → modulo
 *            spento, l'isola wishlist resta intatta. Responsabilità: badge
 *            navbar [data-cart-count]/[data-cart-nav]; count-at-boot; click
 *            delegato [data-cart-move] (bottone "Sposta nel carrello" sulla
 *            pagina /wishlist) OTTIMISTICO con rollback; eventi
 *            egi:cart:changed; annunci via live region unica [data-wishlist-live]
 *            (decisione M-373); dopo ogni move riuscito → resync dello store
 *            wishlist (il move sposta un'unità tra DUE badge). aria-busy MAI
 *            disabled (focus mai rubato, WCAG 2.2 §2.4.7 A). API:
 *            window.egiCart.refresh.
 */

import { isAbortError } from '../wishlist/server-driver';
import { initCartPage } from './cart-page';
import { focusBeforeRowRemoval } from './row-focus';
import { CartServerDriver } from './server-driver';
import {
    CART_CHANGED_EVENT,
    CartRefusalError,
    isCartConfig,
    type CartChangedAction,
    type CartChangedDetail,
    type CartLabels,
} from './types';

export interface CartIslandDeps {
    authenticated: boolean;
    announce: (message: string) => void;
    /** Riallinea lo store wishlist al server dopo un move cross-lista (design §4). */
    wishlistResync: () => Promise<void>;
}

function updateBadge(count: number, labels: CartLabels): void {
    document.querySelectorAll<HTMLElement>('[data-cart-count]').forEach((el) => {
        el.textContent = count > 99 ? '99+' : String(count);
        // Gotcha organo: visibilità via display, MAI attributo hidden battuto dalle utility.
        el.style.display = count > 0 ? 'inline-block' : 'none';
    });
    document.querySelectorAll<HTMLElement>('[data-cart-nav]').forEach((link) => {
        link.setAttribute(
            'aria-label',
            count > 0 ? labels.navCount.replace(':count', String(count)) : labels.navLabel,
        );
    });
}

/**
 * Estensione cart dell'isola: riceve la sezione `cart` (raw) del blocco
 * #wishlist-config e le dipendenze condivise dall'entry wishlist-island.
 */
export function initCartIsland(rawCartSection: unknown, deps: CartIslandDeps): void {
    if (!deps.authenticated || !isCartConfig(rawCartSection)) {
        return; // ADR-C7: niente cart guest; sezione assente/malformata → modulo spento
    }

    const config = rawCartSection;
    const labels = config.labels;
    const driver = new CartServerDriver(config.endpoints);
    let cartCount = 0;

    function emit(action: CartChangedAction, count: number, id?: string): void {
        const detail: CartChangedDetail = { count, action };
        if (id !== undefined) {
            detail.id = id;
        }
        document.dispatchEvent(new CustomEvent<CartChangedDetail>(CART_CHANGED_EVENT, { detail }));
    }

    document.addEventListener(CART_CHANGED_EVENT, (event) => {
        const detail = (event as CustomEvent<CartChangedDetail>).detail;
        cartCount = detail.count;
        updateBadge(detail.count, labels);
    });

    /** Move-to-cart dalla pagina /wishlist: ottimistico con rollback (design §4). */
    async function onMoveClick(btn: HTMLElement): Promise<void> {
        const id = btn.dataset.egiId ?? '';
        if (id === '' || btn.getAttribute('aria-busy') === 'true') {
            return;
        }

        const row = btn.closest<HTMLElement>('[data-wishlist-item]');
        const list = row?.closest<HTMLElement>('[data-wishlist-list]') ?? null;
        const previousCount = cartCount;
        let restoreRow: (() => void) | null = null;

        btn.setAttribute('aria-busy', 'true'); // mai disabled: il focus resta (§7.5 M-373)
        btn.style.opacity = '0.6';

        // Ottimistico: la riga esce dalla wishlist, il badge cart sale subito
        if (row && list) {
            const parent = row.parentElement;
            const anchor = row.nextElementSibling;
            focusBeforeRowRemoval(
                list, row, '[data-wishlist-item]', '[data-cart-move], [data-wishlist-remove]',
                document.querySelector<HTMLElement>('[data-wishlist-heading]'),
            );
            row.remove();
            restoreRow = (): void => {
                parent?.insertBefore(row, anchor);
            };
        }
        emit('add', previousCount + 1, id);

        try {
            const result = await driver.add(id);
            emit('sync', result.cartCount, id); // conteggio autoritativo dal server
            deps.announce(result.message ?? labels.added);
            await deps.wishlistResync(); // secondo badge + contatore pagina (move = 2 contatori)
        } catch (error) {
            if (isAbortError(error)) {
                return; // superata da una richiesta più recente: decide quella
            }
            restoreRow?.();
            emit('sync', previousCount, id);
            deps.announce(
                error instanceof CartRefusalError && error.message !== '' ? error.message : labels.error,
            );
        } finally {
            btn.removeAttribute('aria-busy');
            btn.style.opacity = '';
        }
    }

    // Click delegato su document (contratto isole EGI: righe iniettate ereditano).
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const btn = target.closest<HTMLElement>('[data-cart-move]');
        if (btn) {
            event.preventDefault();
            void onMoveClick(btn);
        }
    });

    function refresh(): void {
        void driver
            .count()
            .then((count) => {
                emit('sync', count);
            })
            .catch(() => {
                // errore già passato dal ponte UEM nel driver; il badge resta com'è
            });
    }

    // Boot: count leggero (il badge parte dal server, mai da localStorage — ADR-C7)
    refresh();

    initCartPage({
        driver,
        labels,
        announce: deps.announce,
        getCount: () => cartCount,
        emit,
        wishlistResync: deps.wishlistResync,
    });

    window.egiCart = { refresh };
}
